import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  type WalletClient,
} from "viem";
import { parseUnits } from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import {
  EIP712Domain,
  Pair,
  withSignatureErrorHandler,
} from "#/utils/signature";

import { deposit } from "../kyan.service";
import {
  type DepositRequest,
  type DepositResponse,
  type SignedUserDeposit,
} from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";

const UserDeposit = [
  { name: "deadline", type: "uint256" },
  { name: "to", type: "address" },
  { name: "from", type: "address" },
  { name: "amount", type: "uint256" },
  { name: "pair", type: "Pair" },
];

export const signDeposit = async (
  depositRequest: Omit<DepositRequest, "signature" | "signature_deadline">,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<SignedUserDeposit> => {
  const signature_deadline = getSignatureDeadline();

  return {
    ...depositRequest,
    signature_deadline,
    signature: await walletClient.signTypedData({
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { UserDeposit, Pair },
      primaryType: "UserDeposit",
      message: {
        to: depositRequest.to,
        from: depositRequest.from,
        amount: parseUnits(depositRequest.amount.toString(), 6),
        pair: depositRequest.pair,
        deadline: signature_deadline,
      },
    }),
  };
};

export const validateDepositSignature = async (
  payload: SignedUserDeposit,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return await publicClient.verifyTypedData({
    address: payload.from,
    domain: EIP712Domain[chainId],
    types: { UserDeposit, Pair },
    primaryType: "UserDeposit",
    message: {
      to: payload.to,
      from: payload.from,
      amount: parseUnits(payload.amount.toString(), 6),
      pair: payload.pair,
      deadline: payload.signature_deadline,
    },
    signature: payload.signature,
  });
};

export const useDeposit = (
  options?: UseMutationOptions<
    DepositResponse,
    Error,
    Omit<DepositRequest, "signature_deadline" | "signature">
  > & {
    onPendingDeposit?: (signedDeposit: SignedUserDeposit) => void;
  },
) => {
  const { walletClient } = useWalletClient();

  return useMutation({
    ...options,
    mutationFn: async ({ to, from, amount, pair }) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const signedDeposit = await withSignatureErrorHandler(async () =>
        signDeposit(
          {
            to,
            from,
            amount,
            pair,
          },
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        ),
      );

      const isSignatureValid = await validateDepositSignature(
        signedDeposit,
        walletClient.chain?.id as ChainId,
      );

      options?.onPendingDeposit?.(signedDeposit);

      return defaultApiResponseHandler({
        serviceName: "Deposit",
        payload: signedDeposit,
        service: deposit,
        validation: [isSignatureValid],
      });
    },
  });
};
